// Communication Phase 3 — Slice 3: Unit Message (UM) tenant screen.
// Private unit-to-unit messaging. Names hidden until a request is accepted.
// No read receipts / seen / typing / presence. Reuses the assistant session.
import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Send, Plus, ChevronLeft, MessageSquare, Check, X, RotateCcw } from 'lucide-react';
import { ScreenShell, DEFAULT_SURFACE } from './AssistantScreens';
import PhotoAttach from '../reviewShield/PhotoAttach';

export default function UnitMessageScreen({ cardId, token, api, tx, isCondo, myUnit, onBack, surface }) {
    const base = api || (typeof window !== 'undefined' ? window.location.origin : '');
    const roomLabel = isCondo ? tx.umUnit : tx.umRoom;
    const cardCls = surface || DEFAULT_SURFACE;

    const [view, setView] = useState('list'); // list | new | thread
    const [convs, setConvs] = useState(null);
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState('');
    const [firstMsg, setFirstMsg] = useState('');
    const [active, setActive] = useState(null); // conversation view for thread
    const [msgs, setMsgs] = useState(null);
    const [text, setText] = useState('');
    const [threadPhotos, setThreadPhotos] = useState([]);
    const [lightbox, setLightbox] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const endRef = useRef(null);

    const q = `token=${encodeURIComponent(token || '')}`;
    const apiGet = useCallback((p) => fetch(`${base}/api/assistant/${cardId}/um${p}${p.includes('?') ? '&' : '?'}${q}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(r))), [base, cardId, q]);
    const apiPost = useCallback(async (p, body) => {
        const r = await fetch(`${base}/api/assistant/${cardId}/um${p}?${q}`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: body ? JSON.stringify(body) : undefined,
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw data;
        return data;
    }, [base, cardId, q]);

    const loadConvs = useCallback(() => {
        setConvs(null);
        apiGet('/conversations').then((d) => setConvs(d.conversations || [])).catch(() => setConvs([]));
    }, [apiGet]);

    useEffect(() => { if (view === 'list') loadConvs(); }, [view, loadConvs]);
    useEffect(() => { endRef.current?.scrollIntoView({ block: 'nearest' }); }, [msgs]);

    // Two-way live updates via lightweight 5s polling (no WebSockets/SSE).
    // Silent refresh (no loading flicker); React reconciles messages by id.
    // Exactly one interval; cleaned up on unmount / view change.
    useEffect(() => {
        if (view === 'thread' && active?.id) {
            const id = active.id;
            const t = setInterval(() => {
                apiGet(`/conversations/${id}/messages`)
                    .then((d) => {
                        setActive((prev) => (prev && prev.id === id ? (d.conversation || prev) : prev));
                        setMsgs(d.messages || []);
                    })
                    .catch(() => {});
            }, 5000);
            return () => clearInterval(t);
        }
        if (view === 'list') {
            const t = setInterval(() => {
                apiGet('/conversations').then((d) => setConvs(d.conversations || [])).catch(() => {});
            }, 5000);
            return () => clearInterval(t);
        }
    }, [view, active?.id, apiGet]);

    const openNew = () => {
        setError(''); setSelectedUnit(''); setFirstMsg('');
        apiGet('/units').then((d) => setUnits(d.units || [])).catch(() => setUnits([]));
        setView('new');
    };

    const sendRequest = async () => {
        if (!selectedUnit || busy) return;
        setBusy(true); setError('');
        try {
            await apiPost('/conversations', { target_unit: selectedUnit, body: firstMsg.trim() || null });
            setView('list');
        } catch (e) {
            setError(e?.detail || tx.umError);
        } finally { setBusy(false); }
    };

    const openThread = (conv) => {
        setActive(conv); setMsgs(null); setText(''); setThreadPhotos([]); setError('');
        setView('thread');
        apiGet(`/conversations/${conv.id}/messages`).then((d) => {
            setActive(d.conversation || conv); setMsgs(d.messages || []);
        }).catch(() => setMsgs([]));
    };

    const refreshThread = useCallback((id) => apiGet(`/conversations/${id}/messages`).then((d) => {
        setActive(d.conversation); setMsgs(d.messages || []);
    }).catch(() => {}), [apiGet]);

    const act = async (conv, action) => {
        setBusy(true); setError('');
        try {
            const res = await apiPost(`/conversations/${conv.id}/${action}`);
            if (view === 'thread') { setActive(res); await refreshThread(conv.id); }
            else loadConvs();
        } catch (e) {
            setError(e?.detail || tx.umError);
            if (view === 'list') loadConvs();
        } finally { setBusy(false); }
    };

    const sendMsg = async (e) => {
        e.preventDefault();
        const body = text.trim();
        if ((!body && !threadPhotos.length) || busy || active?.status !== 'accepted') return;
        setBusy(true);
        try {
            let photo_ids = [];
            if (threadPhotos.length) {
                const fd = new FormData();
                threadPhotos.forEach((p) => fd.append('photos', p.file));
                const r = await fetch(`${base}/api/assistant/${cardId}/um/conversations/${active.id}/photos?${q}`,
                    { method: 'POST', body: fd });
                const d = await r.json().catch(() => ({}));
                if (!r.ok) throw d;
                photo_ids = d.photo_ids || [];
            }
            await apiPost(`/conversations/${active.id}/messages`, { body: body || null, photo_ids });
            setText(''); setThreadPhotos([]);
            await refreshThread(active.id);
        } catch (err) { setError(err?.detail || tx.umError); }
        finally { setBusy(false); }
    };

    const photoUrl = (pid) => `${base}/api/assistant/${cardId}/um/photo/${pid}?${q}`;

    const otherLabel = (c) => (c.other_name
        ? `${c.other_name} · ${roomLabel} ${c.other_unit}`
        : `${roomLabel} ${c.other_unit}`);

    // ── NEW MESSAGE ──
    if (view === 'new') {
        return (
            <ScreenShell title={tx.umStart} onBack={() => setView('list')} tx={tx} testId="um-new-screen">
                <div className={`${cardCls} rounded-2xl p-4`}>
                    <p className="text-sm text-slate-300 mb-3">{tx.umChooseUnit}</p>
                    {units.length === 0 ? (
                        <p className="text-sm text-slate-500 py-4 text-center" data-testid="um-no-units">{tx.umNoUnits}</p>
                    ) : (
                        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="um-unit-list">
                            {units.map((u) => (
                                <button key={u.unit} type="button" onClick={() => setSelectedUnit(u.unit)}
                                    data-testid={`um-unit-${u.unit}`}
                                    className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors ${
                                        selectedUnit === u.unit
                                            ? 'bg-emerald-600 border-emerald-500 text-white'
                                            : 'bg-white/5 border-white/15 text-slate-200 hover:bg-white/10'}`}>
                                    {roomLabel} {u.unit}
                                </button>
                            ))}
                        </div>
                    )}
                    {selectedUnit && (
                        <>
                            <textarea value={firstMsg} onChange={(e) => setFirstMsg(e.target.value)}
                                data-testid="um-first-message" placeholder={tx.umFirstMessagePlaceholder} rows={3}
                                className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 mb-3" />
                            <button type="button" onClick={sendRequest} disabled={busy}
                                data-testid="um-send-request-btn"
                                className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold flex items-center justify-center gap-2">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                {tx.umSendRequest}
                            </button>
                        </>
                    )}
                    {error && <p className="text-xs text-red-400 mt-3 text-center" data-testid="um-error">{error}</p>}
                </div>
            </ScreenShell>
        );
    }

    // ── THREAD ──
    if (view === 'thread' && active) {
        const isPendingIncoming = active.role === 'incoming';
        return (
            <ScreenShell title={otherLabel(active)} onBack={() => setView('list')} tx={tx} testId="um-thread-screen">
                <div className={`${cardCls} rounded-2xl p-4`}>
                    <div className="max-h-72 overflow-y-auto space-y-2 pr-1 mb-3" data-testid="um-thread-list">
                        {msgs === null ? (
                            <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-500" /></div>
                        ) : msgs.length === 0 ? (
                            <p className="text-xs text-slate-500 py-4 text-center">{active.status === 'accepted' ? '' : tx.umNotActive}</p>
                        ) : msgs.map((m) => {
                            const mine = String(m.sender_unit) === String(myUnit);
                            return (
                                <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`} data-testid={`um-msg-${m.id}`}>
                                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? 'bg-emerald-600/25 border border-emerald-500/30' : 'bg-slate-800/70 border border-slate-700'}`}>
                                        {m.body && <p className="text-sm text-slate-100 whitespace-pre-wrap break-words">{m.body}</p>}
                                        {m.photo_ids?.length > 0 && (
                                            <div className="grid grid-cols-2 gap-1.5 mt-1.5" data-testid={`um-msg-photos-${m.id}`}>
                                                {m.photo_ids.map((pid) => (
                                                    <button key={pid} type="button" onClick={() => setLightbox(photoUrl(pid))}
                                                        className="aspect-square rounded-lg overflow-hidden border border-white/10"
                                                        data-testid={`um-msg-photo-${pid}`}>
                                                        <img src={photoUrl(pid)} alt="" loading="lazy" className="w-full h-full object-cover" />
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {mine && m.read_at && (
                                        <span className="self-end text-[10px] text-emerald-400/80 mt-0.5 mr-1" data-testid={`um-msg-seen-${m.id}`}>{tx.umSeen}</span>
                                    )}
                                </div>
                            );
                        })}
                        <div ref={endRef} />
                    </div>

                    {isPendingIncoming && (
                        <div className="flex gap-2 mb-2" data-testid="um-accept-ignore">
                            <button type="button" onClick={() => act(active, 'accept')} disabled={busy}
                                data-testid="um-accept-btn"
                                className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold flex items-center justify-center gap-1.5">
                                <Check className="w-4 h-4" /> {tx.umAccept}
                            </button>
                            <button type="button" onClick={() => act(active, 'ignore')} disabled={busy}
                                data-testid="um-ignore-btn"
                                className="flex-1 py-2.5 rounded-xl bg-white/5 border border-white/15 hover:bg-white/10 disabled:opacity-50 text-slate-200 font-semibold flex items-center justify-center gap-1.5">
                                <X className="w-4 h-4" /> {tx.umIgnore}
                            </button>
                        </div>
                    )}

                    {active.role === 'outgoing' && (
                        <p className="text-xs text-slate-400 text-center py-2" data-testid="um-waiting">{tx.umWaiting}</p>
                    )}

                    {active.status === 'accepted' && (
                        <>
                            <div className="mb-2" data-testid="um-thread-photo">
                                <PhotoAttach photos={threadPhotos} onChange={setThreadPhotos} helperText={tx.umPhotoHelper} />
                            </div>
                            <form onSubmit={sendMsg} className="flex items-center gap-2">
                                <input value={text} onChange={(e) => setText(e.target.value)}
                                    data-testid="um-thread-input" placeholder={tx.umMessagePlaceholder}
                                    className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500" />
                                <button type="submit" disabled={busy || (!text.trim() && !threadPhotos.length)} data-testid="um-thread-send"
                                    className="shrink-0 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-lg px-3 py-2">
                                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                </button>
                            </form>
                        </>
                    )}

                    {active.status === 'accepted' && (
                        <button type="button" onClick={() => act(active, 'close')} disabled={busy}
                            data-testid="um-close-btn"
                            className="mt-3 w-full py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-slate-300 text-sm">
                            {tx.umClose}
                        </button>
                    )}

                    {active.status === 'closed' && (
                        <div className="text-center">
                            <p className="text-xs text-slate-500 py-2" data-testid="um-closed-note">{tx.umClosedNote}</p>
                            {active.can_reopen && (
                                <button type="button" onClick={() => act(active, 'reopen')} disabled={busy}
                                    data-testid="um-reopen-btn"
                                    className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold flex items-center justify-center gap-1.5">
                                    <RotateCcw className="w-4 h-4" /> {tx.umReopen}
                                </button>
                            )}
                            {active.can_reopen && <p className="text-[11px] text-slate-500 mt-1">{tx.umReopenHint}</p>}
                        </div>
                    )}
                    {error && <p className="text-xs text-red-400 mt-3 text-center" data-testid="um-error">{error}</p>}
                </div>
                {lightbox && (
                    <div onClick={() => setLightbox(null)} data-testid="um-lightbox"
                        className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4">
                        <button type="button" onClick={() => setLightbox(null)} data-testid="um-lightbox-close"
                            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/70 text-white flex items-center justify-center">
                            <X className="w-5 h-5" />
                        </button>
                        <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
                    </div>
                )}
            </ScreenShell>
        );
    }

    // ── LIST ──
    const incoming = (convs || []).filter((c) => c.role === 'incoming');
    const outgoing = (convs || []).filter((c) => c.role === 'outgoing');
    const activeC = (convs || []).filter((c) => c.role === 'active');
    const closed = (convs || []).filter((c) => c.role === 'closed');

    const Row = ({ c, badge }) => (
        <button type="button" onClick={() => openThread(c)} data-testid={`um-conv-${c.id}`}
            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors text-left">
            <MessageSquare className="w-5 h-5 text-emerald-400 shrink-0" />
            <div className="min-w-0 flex-1">
                <p className="text-sm text-white font-medium truncate">{otherLabel(c)}</p>
                {badge && <p className="text-[11px] text-slate-400">{badge}</p>}
            </div>
        </button>
    );

    return (
        <ScreenShell title={tx.umTitle} onBack={onBack} tx={tx} testId="um-list-screen">
            <button type="button" onClick={openNew} data-testid="um-start-btn"
                className="w-full mb-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> {tx.umStart}
            </button>
            {convs === null ? (
                <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-500" /></div>
            ) : (convs.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center" data-testid="um-empty">{tx.umNone}</p>
            ) : (
                <div className="space-y-4">
                    {incoming.length > 0 && (
                        <div data-testid="um-section-requests">
                            <p className="text-xs font-semibold text-amber-300 uppercase tracking-wide mb-2">{tx.umRequests}</p>
                            <div className="space-y-2">{incoming.map((c) => <Row key={c.id} c={c} badge={`${roomLabel} ${c.other_unit} ${tx.umWantsToMessage}`} />)}</div>
                        </div>
                    )}
                    {activeC.length > 0 && (
                        <div data-testid="um-section-active">
                            <p className="text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-2">{tx.umActive}</p>
                            <div className="space-y-2">{activeC.map((c) => <Row key={c.id} c={c} />)}</div>
                        </div>
                    )}
                    {outgoing.length > 0 && (
                        <div data-testid="um-section-sent">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{tx.umSent}</p>
                            <div className="space-y-2">{outgoing.map((c) => <Row key={c.id} c={c} badge={tx.umWaiting} />)}</div>
                        </div>
                    )}
                    {closed.length > 0 && (
                        <div data-testid="um-section-closed">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{tx.umClosed}</p>
                            <div className="space-y-2">{closed.map((c) => <Row key={c.id} c={c} />)}</div>
                        </div>
                    )}
                </div>
            ))}
        </ScreenShell>
    );
}
